import { checkinsApi } from './api'

export interface OfflineCheckIn {
  id: string
  type: 'checkin' | 'checkout'
  locationId: string
  latitude: number
  longitude: number
  accuracy?: number
  notes?: string
  timestamp: Date
  synced: boolean
}

export interface OfflineReportDraft {
  id: string
  type: 'incident' | 'hourly'
  reportId?: string // Server-side ID if it exists
  localId: string // Always unique local identifier
  data: any // Report form data
  mediaFiles: OfflineMediaFile[]
  timestamp: Date
  lastModified: Date
  synced: boolean
  syncedAt?: Date
  serverVersion?: Date // When data was last updated on server
}

export interface OfflineMediaFile {
  id: string
  type: 'image' | 'video'
  localPath: string
  file: File
  uploadedUrl?: string
  synced: boolean
}

export interface CurrentCheckInSnapshot {
  id: string
  location: string
  locationId: string
  checkInTime: string
  notes?: string
}

class OfflineStorageService {
  private readonly STORAGE_KEY = 'offline_checkins'
  private readonly REPORTS_STORAGE_KEY = 'offline_reports'
  private readonly MEDIA_STORAGE_KEY = 'offline_media'
  private readonly CURRENT_CHECKIN_STORAGE_KEY = 'current_checkin_snapshot'
  private readonly SYNC_INTERVAL = 120000 // 2 minutes (reduced frequency to prevent spam)
  private syncInterval: NodeJS.Timeout | null = null

  constructor() {
    this.startSyncInterval()
  }

  // Current check-in snapshot helpers
  setCurrentCheckInSnapshot(snapshot: CurrentCheckInSnapshot): void {
    try {
      localStorage.setItem(this.CURRENT_CHECKIN_STORAGE_KEY, JSON.stringify(snapshot))
      console.log('📱 Saved current check-in snapshot:', snapshot)
    } catch (error) {
      console.error('Error saving current check-in snapshot:', error)
    }
  }

  getCurrentCheckInSnapshot(): CurrentCheckInSnapshot | null {
    try {
      const raw = localStorage.getItem(this.CURRENT_CHECKIN_STORAGE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch (error) {
      console.error('Error reading current check-in snapshot:', error)
      return null
    }
  }

  clearCurrentCheckInSnapshot(): void {
    try {
      localStorage.removeItem(this.CURRENT_CHECKIN_STORAGE_KEY)
      console.log('📱 Cleared current check-in snapshot')
    } catch (error) {
      console.error('Error clearing current check-in snapshot:', error)
    }
  }

  // Store a check-in or check-out for offline sync
  async storeOfflineAction(action: Omit<OfflineCheckIn, 'id' | 'timestamp' | 'synced'>): Promise<void> {
    const offlineAction: OfflineCheckIn = {
      ...action,
      id: crypto.randomUUID(),
      timestamp: new Date(),
      synced: false
    }

    const existing = this.getOfflineActions()
    existing.push(offlineAction)
    this.saveOfflineActions(existing)

    console.log('📱 Stored offline action:', offlineAction)
  }

  // Get all offline actions
  getOfflineActions(): OfflineCheckIn[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error reading offline actions:', error)
      return []
    }
  }

  // Save offline actions to localStorage
  private saveOfflineActions(actions: OfflineCheckIn[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(actions))
    } catch (error) {
      console.error('Error saving offline actions:', error)
    }
  }

  // Check if we're online
  isOnline(): boolean {
    return navigator.onLine
  }

  // Report draft methods
  async storeOfflineReportDraft(draft: Omit<OfflineReportDraft, 'id' | 'localId' | 'timestamp' | 'lastModified' | 'synced'> & { localId?: string }): Promise<string> {
    const localId = draft.localId || crypto.randomUUID()

    const offlineDraft: OfflineReportDraft = {
      ...draft,
      id: draft.reportId || localId,
      localId,
      timestamp: new Date(),
      lastModified: new Date(),
      synced: false
    }

    const existing = this.getOfflineReportDrafts()

    // if localId is provided, update by localId
    const byLocalIdx = existing.findIndex(d => d.localId === localId)
    if (byLocalIdx !== -1) {
      const existingDraft = existing[byLocalIdx]
      const dataChanged = this.hasDataChanged(existingDraft.data, draft.data)

      if (!dataChanged) {
        console.log('📱 No data changes detected, skipping draft update')
        return existingDraft.localId
      }

      existing[byLocalIdx] = {
        ...existingDraft,
        ...offlineDraft,
        id: existingDraft.id, // keep the same id
        localId: existingDraft.localId,
        timestamp: existingDraft.timestamp,
        lastModified: new Date(),
      }

      this.saveOfflineReportDrafts(existing)
      console.log('📱 Updated offline report draft by localId:', existing[byLocalIdx])
      return existing[byLocalIdx].localId
    }

      // if localId is not provided, use the heuristic of (reportId) or (type && !reportId)
    const existingIndex = existing.findIndex(d =>
      (draft.reportId && d.reportId === draft.reportId) ||
      (d.type === draft.type && !d.reportId && !draft.reportId)
    )

    if (existingIndex !== -1) {
      const existingDraft = existing[existingIndex]
      const dataChanged = this.hasDataChanged(existingDraft.data, draft.data)
      if (!dataChanged) {
        console.log('📱 No data changes detected, skipping draft update')
        return existingDraft.localId
      }
      existing[existingIndex] = {
        ...existingDraft,
        ...offlineDraft,
        id: existingDraft.id,
        localId: existingDraft.localId,
        timestamp: existingDraft.timestamp,
        lastModified: new Date()
      }
    } else {
      existing.push(offlineDraft)
    }

    this.saveOfflineReportDrafts(existing)
    console.log('📱 Stored offline report draft:', offlineDraft)
    return localId
  }

  // Helper method to check if data has actually changed
  private hasDataChanged(oldData: any, newData: any): boolean {
    // Remove timestamp fields and other non-content fields for comparison
    const cleanOldData = this.cleanDataForComparison(oldData)
    const cleanNewData = this.cleanDataForComparison(newData)
    
    return JSON.stringify(cleanOldData) !== JSON.stringify(cleanNewData)
  }

  // Helper method to clean data for comparison (remove non-content fields)
  private cleanDataForComparison(data: any): any {
    if (!data) return data
    
    const cleaned = { ...data }
    
    // Remove fields that don't represent actual content changes
    const fieldsToRemove = [
      'updatedAt', 'createdAt', 'syncedAt', 'serverVersion',
      'lastModified', 'timestamp', 'id', 'localId'
    ]
    
    fieldsToRemove.forEach(field => {
      delete cleaned[field]
    })
    
    return cleaned
  }

  // Update offline report draft by localId (merge inmutable)
  updateOfflineReportDraft(localId: string, partial: Partial<OfflineReportDraft> & { data?: any }) {
    const drafts = this.getOfflineReportDrafts()
    const idx = drafts.findIndex(d => d.localId === localId)
    if (idx === -1) return false

    const prev = drafts[idx]

    drafts[idx] = {
      ...prev,
      ...partial,
      data: partial.data ? { ...prev.data, ...partial.data } : prev.data,
      lastModified: new Date()
    }

    this.saveOfflineReportDrafts(drafts)
    return true
  }

  // Update offline report draft by reportId (merge inmutable)
  updateOfflineReportDraftByReportId(reportId: string, partial: Partial<OfflineReportDraft> & { data?: any }) {
    const drafts = this.getOfflineReportDrafts()
    const idx = drafts.findIndex(d => d.reportId === reportId)
    if (idx === -1) return false

    const prev = drafts[idx]

    drafts[idx] = {
      ...prev,
      ...partial,
      data: partial.data ? { ...prev.data, ...partial.data } : prev.data,
      lastModified: new Date()
    }

    this.saveOfflineReportDrafts(drafts)
    return true
  }

  getOfflineReportDrafts(): OfflineReportDraft[] {
    try {
      const stored = localStorage.getItem(this.REPORTS_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error reading offline report drafts:', error)
      return []
    }
  }

  private saveOfflineReportDrafts(drafts: OfflineReportDraft[]): void {
    try {
      localStorage.setItem(this.REPORTS_STORAGE_KEY, JSON.stringify(drafts))
    } catch (error) {
      console.error('Error saving offline report drafts:', error)
    }
  }

  async storeOfflineMediaFile(file: File, type: 'image' | 'video'): Promise<string> {
    const mediaId = crypto.randomUUID()
    
    try {
      // Store actual file data in IndexedDB
      await this.storeFileInIndexedDB(mediaId, file)
      
      const mediaFile: Omit<OfflineMediaFile, 'file'> = {
        id: mediaId,
        type,
        localPath: `offline_${mediaId}_${file.name}`,
        synced: false
      }

      // Store metadata in localStorage
      const existing = this.getOfflineMediaFiles()
      existing.push(mediaFile as OfflineMediaFile)
      this.saveOfflineMediaFiles(existing)

      console.log('📱 Stored offline media file:', mediaFile)
      return mediaId
    } catch (error) {
      console.error('📱 Error storing media file:', error)
      throw error
    }
  }

  // IndexedDB operations for file storage
  private async storeFileInIndexedDB(id: string, file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('OfflineFiles', 1)
      
      request.onerror = () => reject(request.error)
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains('files')) {
          db.createObjectStore('files', { keyPath: 'id' })
        }
      }
      
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readwrite')
        const store = transaction.objectStore('files')
        
        // Convert file to arrayBuffer for storage
        const reader = new FileReader()
        reader.onload = () => {
          const fileData = {
            id,
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }
          
          const putRequest = store.put(fileData)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(putRequest.error)
        }
        reader.onerror = () => reject(reader.error)
        reader.readAsArrayBuffer(file)
      }
    })
  }

  async getFileFromIndexedDB(id: string): Promise<File | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('OfflineFiles', 1)
      
      request.onerror = () => reject(request.error)
      
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readonly')
        const store = transaction.objectStore('files')
        
        const getRequest = store.get(id)
        getRequest.onsuccess = () => {
          const result = getRequest.result
          if (result) {
            const file = new File([result.data], result.name, { type: result.type })
            resolve(file)
          } else {
            resolve(null)
          }
        }
        getRequest.onerror = () => reject(getRequest.error)
      }
    })
  }

  getOfflineMediaFiles(): OfflineMediaFile[] {
    try {
      const stored = localStorage.getItem(this.MEDIA_STORAGE_KEY)
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error reading offline media files:', error)
      return []
    }
  }

  private saveOfflineMediaFiles(files: OfflineMediaFile[]): void {
    try {
      // Store only metadata, file data is in IndexedDB
      const fileRefs = files.map(f => ({
        id: f.id,
        type: f.type,
        localPath: f.localPath,
        uploadedUrl: f.uploadedUrl,
        synced: f.synced
      }))
      localStorage.setItem(this.MEDIA_STORAGE_KEY, JSON.stringify(fileRefs))
    } catch (error) {
      console.error('Error saving offline media files:', error)
    }
  }

  // Sync all pending offline actions
  async syncOfflineActions(): Promise<{ success: number; failed: number }> {
    if (!this.isOnline()) {
      console.log('📱 Offline - skipping sync')
      return { success: 0, failed: 0 }
    }

    const checkInResults = await this.syncOfflineCheckIns()
    const reportResults = await this.syncOfflineReports()

    return {
      success: checkInResults.success + reportResults.success,
      failed: checkInResults.failed + reportResults.failed
    }
  }

  private async syncOfflineCheckIns(): Promise<{ success: number; failed: number }> {
    const actions = this.getOfflineActions().filter(action => !action.synced)
    if (actions.length === 0) {
      return { success: 0, failed: 0 }
    }

    console.log(`📱 Syncing ${actions.length} offline check-in actions...`)

    let success = 0
    let failed = 0

    for (const action of actions) {
      try {
        if (action.type === 'checkin') {
          await checkinsApi.checkIn({
            locationId: action.locationId,
            latitude: action.latitude,
            longitude: action.longitude,
            accuracy: action.accuracy,
            notes: action.notes
          })
        } else if (action.type === 'checkout') {
          // For checkout, we need to get the current check-in ID from the server
          // since we only stored the locationId in offline mode
          const currentCheckIn = await checkinsApi.getCurrent()
          if (currentCheckIn && currentCheckIn.id) {
            await checkinsApi.checkOut(currentCheckIn.id, {
              notes: action.notes
            })
          } else {
            console.warn('📱 No active check-in found for offline checkout sync')
            // Mark as synced anyway to avoid infinite retry
            action.synced = true
          }
        }

        // Mark as synced
        action.synced = true
        success++
        console.log(`📱 Synced ${action.type}:`, action.id)
      } catch (error) {
        console.error(`📱 Failed to sync ${action.type}:`, error)
        failed++
      }
    }

    // actions are already modified in memory
    this.saveOfflineActions(actions)

    // Remove synced actions using the same array
    const remaining = actions.filter(action => !action.synced)
    this.saveOfflineActions(remaining)

    console.log(`📱 Check-in sync complete: ${success} successful, ${failed} failed`)
    return { success, failed }
  }

  private async syncOfflineReports(): Promise<{ success: number; failed: number }> {
    const drafts = this.getOfflineReportDrafts().filter(draft => !draft.synced)
    if (drafts.length === 0) {
      return { success: 0, failed: 0 }
    }

    console.log(`📱 Syncing ${drafts.length} offline report drafts...`)

    let success = 0
    let failed = 0

    for (const draft of drafts) {
      try {
        // Check if this draft has been synced recently and hasn't changed
        if (draft.syncedAt && draft.serverVersion) {
          const timeSinceLastSync = Date.now() - new Date(draft.syncedAt).getTime()
          const timeSinceServerUpdate = Date.now() - new Date(draft.serverVersion).getTime()
          
          // If synced within last 5 minutes and server hasn't been updated, skip
          if (timeSinceLastSync < 5 * 60 * 1000 && timeSinceServerUpdate > 5 * 60 * 1000) {
            console.log(`📱 Skipping sync for ${draft.type} report (recently synced):`, draft.localId)
            draft.synced = true
            success++
            continue
          }
        }
        
        await this.syncReportDraft(draft)
        draft.synced = true
        draft.syncedAt = new Date()
        success++
        console.log(`📱 Synced ${draft.type} report:`, draft.localId)
      } catch (error) {
        console.error(`📱 Failed to sync ${draft.type} report:`, error)
        failed++
      }
    }

    // Update storage with synced status
    this.saveOfflineReportDrafts(drafts)

    // Remove synced drafts using the same array
    const remaining = drafts.filter(draft => !draft.synced)
    this.saveOfflineReportDrafts(remaining)

    console.log(`📱 Report sync complete: ${success} successful, ${failed} failed`)
    return { success, failed }
  }

  private async syncReportDraft(draft: OfflineReportDraft): Promise<void> {
    const { reportsApi } = await import('./api')
    
    try {
      // Always create a new draft when syncing from offline
      const response = draft.type === 'incident' 
        ? await reportsApi.createIncidentReportViaReports(draft.data)
        : await reportsApi.createHourlyReportViaReports(draft.data)
      
      // Update local draft with server ID and mark for removal
      draft.reportId = response.id
      draft.synced = true
      
      // Clear the local draft since it's now on the server
      this.clearReportDraft(draft.localId)
      
      // Update server version timestamp
      draft.serverVersion = new Date()
      
      // Sync any media files
      for (const mediaFile of draft.mediaFiles) {
        if (!mediaFile.synced) {
          try {
            // Get file from IndexedDB
            const file = await this.getFileFromIndexedDB(mediaFile.id)
            if (file) {
              // Upload media file to server
              // This would require implementing file upload API endpoints
              console.log('📱 Media file ready for upload:', mediaFile.id, file.name)
              // TODO: Implement actual file upload to server
              // For now, mark as synced
              mediaFile.synced = true
            }
          } catch (error) {
            console.error('📱 Error syncing media file:', error)
          }
        }
      }
    } catch (error) {
      console.error('📱 Error syncing report draft:', error)
      throw error
    }
  }

  // Conflict resolution: merge local and server data intelligently
  async resolveReportConflicts(localDraft: OfflineReportDraft, serverData: any): Promise<any> {
    // If server data is newer, use server data
    if (serverData.updatedAt && localDraft.serverVersion) {
      const serverTime = new Date(serverData.updatedAt)
      const localServerTime = new Date(localDraft.serverVersion)
      
      if (serverTime > localServerTime) {
        console.log('📱 Server data is newer, using server version')
        return serverData
      }
    }

    // If local data is newer or server version unknown, merge intelligently
    const merged = { ...serverData }
    
    // Merge non-empty fields from local draft
    Object.keys(localDraft.data).forEach(key => {
      const localValue = localDraft.data[key]
      const serverValue = serverData[key]
      
      // Use local value if it's not empty and different from server
      if (localValue !== null && localValue !== undefined && localValue !== '' && 
          localValue !== serverValue) {
        merged[key] = localValue
        console.log(`📱 Using local value for ${key}:`, localValue)
      }
    })

    return merged
  }

  // Get local report draft for a specific type/ID
  getLocalReportDraft(type: 'incident' | 'hourly', reportId?: string): OfflineReportDraft | null {
    const drafts = this.getOfflineReportDrafts()
    return drafts.find(d => 
      d.type === type && 
      (reportId ? d.reportId === reportId : !d.reportId)
    ) || null
  }

  // Check if we have offline changes for a report
  hasOfflineChanges(type: 'incident' | 'hourly', reportId?: string): boolean {
    const draft = this.getLocalReportDraft(type, reportId)
    return draft !== null && !draft.synced
  }

  // Clear specific report draft
  clearReportDraft(localId: string): void {
    const drafts = this.getOfflineReportDrafts()
    const filtered = drafts.filter(d => d.localId !== localId)
    this.saveOfflineReportDrafts(filtered)
  }

  // Start periodic sync
  private startSyncInterval(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
    }

    this.syncInterval = setInterval(async () => {
      await this.syncOfflineActions()
      
      // Only sync reports if there are unsynced drafts that were modified recently
      const drafts = this.getOfflineReportDrafts().filter(draft => !draft.synced)
      const recentDrafts = drafts.filter(draft => {
        const timeSinceLastModified = Date.now() - new Date(draft.lastModified).getTime()
        return timeSinceLastModified < 10 * 60 * 1000 // Only sync drafts modified in last 10 minutes
      })
      
      if (recentDrafts.length > 0) {
        await this.syncOfflineReports()
      }
      
      // Clean up old drafts periodically (every 10 sync cycles = 20 minutes)
      if (Math.random() < 0.1) { // 10% chance each sync cycle
        await this.cleanupOldDrafts()
      }
      
      // Clean up duplicate drafts occasionally (every 50 sync cycles = ~1.5 hours)
      if (Math.random() < 0.02) { // 2% chance each sync cycle
        await this.cleanupDuplicateDrafts()
      }
    }, this.SYNC_INTERVAL)
  }

  // Stop periodic sync
  stopSyncInterval(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval)
      this.syncInterval = null
    }
  }

  // Get pending sync count
  getPendingSyncCount(): number {
    const checkInCount = this.getOfflineActions().filter(action => !action.synced).length
    const reportCount = this.getOfflineReportDrafts().filter(draft => !draft.synced).length
    return checkInCount + reportCount
  }

  // Clear all offline data (for testing)
  async clearOfflineData(): Promise<void> {
    localStorage.removeItem(this.STORAGE_KEY)
    localStorage.removeItem(this.REPORTS_STORAGE_KEY)
    localStorage.removeItem(this.MEDIA_STORAGE_KEY)
    
    // Also clear IndexedDB files
    try {
      await this.clearIndexedDBFiles()
    } catch (error) {
      console.error('Error clearing IndexedDB files:', error)
    }
    
    console.log('📱 Cleared all offline data')
  }

  // Clean up old drafts that haven't been modified recently
  async cleanupOldDrafts(): Promise<void> {
    const drafts = this.getOfflineReportDrafts()
    const now = new Date()
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000) // 24 hours ago
    
    const oldDrafts = drafts.filter(draft => {
      const lastModified = new Date(draft.lastModified)
      return lastModified < oneDayAgo && draft.synced
    })
    
    if (oldDrafts.length > 0) {
      console.log(`📱 Cleaning up ${oldDrafts.length} old synced drafts`)
      const remainingDrafts = drafts.filter(draft => !oldDrafts.includes(draft))
      this.saveOfflineReportDrafts(remainingDrafts)
    }
  }

  // Clean up duplicate drafts on the server
  async cleanupDuplicateDrafts(): Promise<void> {
    try {
      const { reportsApi } = await import('./api')
      
      // Get all draft types
      const draftTypes = ['incident', 'hourly'] as const
      
      for (const type of draftTypes) {
        try {
          const serverDrafts = type === 'incident' 
            ? await reportsApi.getDraftIncidentReports()
            : await reportsApi.getDraftHourlyReports()
          
          if (Array.isArray(serverDrafts) && serverDrafts.length > 1) {
            console.log(`📱 Found ${serverDrafts.length} ${type} drafts, keeping the most recent one`)
            
            // Sort by creation date, keep the most recent
            const sortedDrafts = serverDrafts.sort((a, b) => 
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            )
            
            const draftToKeep = sortedDrafts[0]
            const draftsToDelete = sortedDrafts.slice(1)
            
            // Delete the older drafts
            for (const draft of draftsToDelete) {
              try {
                if (type === 'incident') {
                  await reportsApi.deleteIncidentReportDraft(draft.id)
                } else {
                  await reportsApi.deleteHourlyReportDraft(draft.id)
                }
                console.log(`📱 Deleted duplicate ${type} draft:`, draft.id)
              } catch (error) {
                console.error(`📱 Failed to delete duplicate ${type} draft:`, error)
              }
            }
          }
        } catch (error) {
          console.error(`📱 Error cleaning up ${type} drafts:`, error)
        }
      }
    } catch (error) {
      console.error('📱 Error in cleanupDuplicateDrafts:', error)
    }
  }

  private async clearIndexedDBFiles(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('OfflineFiles', 1)
      
      request.onerror = () => reject(request.error)
      
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['files'], 'readwrite')
        const store = transaction.objectStore('files')
        
        const clearRequest = store.clear()
        clearRequest.onsuccess = () => resolve()
        clearRequest.onerror = () => reject(clearRequest.error)
      }
    })
  }
}

// Create singleton instance
export const offlineStorage = new OfflineStorageService()

// Listen for online/offline events
window.addEventListener('online', () => {
  console.log('📱 Connection restored - syncing offline actions')
  offlineStorage.syncOfflineActions()
})

window.addEventListener('offline', () => {
  console.log('📱 Connection lost - actions will be stored offline')
}) 